<script setup>
import { ref, watch } from 'vue';
import { reset } from '@formkit/core';

const emit = defineEmits(['submitHandler']);

const props = defineProps({
  buttonText: {
    type: String,
    default: 'Create',
  },
  invoiceRequired: {
    type: String,
    default: 'required',
  },
  accounting: {
    type: Object || null,
    default: {},
  },
  units: {
    type: Array,
    default: [],
  },
});

const claim_date = ref(dayjs().format('YYYY-MM-DD'));

const disabled = ref(false);

const disabledStatus = ['waiting', 'approve', 'paid'];
const canDeleteStatus = ['draft', 'waiting', 'incomplete'];

const action = ref('waiting');
const actionOptions = ref([]);
const canDelete = ref(false);
const isSubmitting = ref(false);

const initial = {
  name: null,
  description: null,
  expense_date: null,
  invoice: [],
  attachments: [],
  unit: 48,
  invoice_twd_amount: null,
  type_of_trip: 'transportation',
  description_of_type: null,
  from: null,
  to: null,
  visit: null,
  invoice_currency: 'USD',
  invoice_exchange_rate: null,
  invoice_amount: null,
};

const formData = ref(JSON.parse(JSON.stringify(initial)));

const errors = ref([]);
const invoiceImage = ref(null);
const attachmentImageArray = ref([]);

// todo use units model
const unitOptions = props.units;

const resetFormData = () => {
  reset('accountingForm');
  formData.value = JSON.parse(JSON.stringify(initial));
};

const handleSubmit = async (data, actionType) => {
  if (isSubmitting.value) return; // 防止重複提交
  isSubmitting.value = true;
  try {
    emit('submitHandler', data, actionType);
    // 不在這裡自動重置 isSubmitting，等待父組件調用 resetFormData
  } catch (error) {
    console.error('Submit error:', error);
    isSubmitting.value = false;
  }
};

const resetSubmitting = () => {
  isSubmitting.value = false;
};

defineExpose({
  resetFormData,
  resetSubmitting,
});

watch(
  () => [formData.value.invoice_exchange_rate, formData.value.invoice_amount],
  ([rate, amount]) => {
    if (rate && amount) {
      formData.value.invoice_twd_amount = Math.floor(rate * amount);
    }
  },
);

watch(
  () => props.accounting,
  value => {
    if (Object.keys(value).length) {
      disabled.value = disabledStatus.includes(value.status);
      actionOptions.value = ['save', 'delete'];
      canDelete.value = canDeleteStatus.includes(value.status);

      formData.value.name = value.name;
      formData.value.description = value.description;
      invoiceImage.value = value.invoice;
      formData.value.unit = value.unit[0]['id'];
      formData.value.invoice = [];
      formData.value.attachments = [];
      invoiceImage.value = value.invoice;
      attachmentImageArray.value = value.attachments;
      formData.value.invoice_twd_amount = value.invoice_twd_amount;
      formData.value.expense_date = dayjs(value.expense_date).format('YYYY-MM-DD');
      formData.value.type_of_trip = value?.trip?.type_of_trip;
      formData.value.description_of_type = value?.trip?.description_of_type;
      formData.value.from = value?.trip?.from;
      formData.value.to = value?.trip?.to;
      formData.value.visit = value?.trip?.visit;
      formData.value.invoice_currency = value.invoice_currency;
      formData.value.invoice_exchange_rate = value.invoice_exchange_rate;
      formData.value.invoice_amount = value.invoice_amount;
    } else {
      formData.value = JSON.parse(JSON.stringify(initial));
      canDelete.value = false;
      actionOptions.value = [];
    }
  },
);
</script>

<template>
  <div class="my-2 text-right">
    <button
      v-if="canDelete"
      @click="$emit('submitHandler', formData, 'delete')"
      :disabled="isSubmitting"
      class="px-2 py-1 text-white rounded bg-rose-400 disabled:opacity-50"
    >
      cancel apply
    </button>
  </div>

  <FormKit
    type="form"
    :disabled="disabled || isSubmitting"
    id="accountingForm"
    v-model="formData"
    :submit-label="buttonText"
    :errors="errors"
    @submit="handleSubmit(formData, action)"
  >
    <div class="mb-2">
      <div class="text-sm font-bold">claim date</div>
      <div class="w-full px-2 py-2 mt-1 border border-gray-400 rounded disabled">
        {{ claim_date }}
      </div>
    </div>

    <FormKit
      type="select"
      label="unit"
      placeholder="Select a unit"
      name="unit"
      :options="unitOptions"
      validation="required"
    />

    <FormKit
      type="text"
      name="name"
      label="Item name"
      placeholder="e.g taxi"
      validation="required"
    />

    <FormKit
      type="textarea"
      name="description"
      label="Description"
      placeholder="e.g Japan"
      validation="required"
    />

    <FormKit
      type="select"
      label="Expense type"
      name="type_of_trip"
      :options="['transportation', 'accommodation', 'meal', 'ticket', 'other']"
    />

    <FormKit
      type="text"
      name="description_of_type"
      label="Description of type"
      placeholder="e.g taxi, airplane"
      validation="required"
    />

    <FormKit
      type="text"
      name="from"
      label="from(location)"
      placeholder="e.g taxi, airplane"
      validation="required"
    />

    <FormKit
      type="text"
      name="to"
      label="to(location)"
      placeholder="e.g taxi, airplane"
      validation="required"
    />
    <FormKit
      type="text"
      name="visit"
      label="Visit"
      placeholder="e.g IBM , IT management Peter Lin"
      validation="required"
    />

    <FormKit
      type="date"
      :value="new Date()"
      name="expense_date"
      label="Expense date"
      validation="required"
      validation-visibility="live"
    />

    <FormKit
      v-show="invoiceImage"
      type="file"
      name="invoice"
      label="Invoice or receipt"
      accept="image/*"
      :help="
        invoiceImage
          ? 'already has invoice, click view to review or upload again'
          : 'click and then take picture of invoice to upload'
      "
      :validation="invoiceRequired"
      multiple
    />
    <div class="mb-2" v-if="invoiceImage">
      <a
        class="px-2 py-1 text-white bg-indigo-400 rounded"
        :href="invoiceImage"
        target="_blank"
        >view</a
      >
    </div>

    <FormKit
      type="file"
      name="attachments"
      label="Your attachments"
      help="except invoice document"
      multiple
    />
    <div class="flex mb-2" v-if="attachmentImageArray.length">
      <template v-for="(item, index) in attachmentImageArray">
        <a
          class="px-2 py-1 ml-2 text-white bg-indigo-400 rounded first:ml-0"
          :href="item"
          target="_blank"
          >{{ `file ${index + 1}` }}
        </a>
      </template>
    </div>

    <FormKit
      type="select"
      label="Currency"
      help="Currency on invoice(add currency in description, if choice other)"
      name="invoice_currency"
      :options="['USD', 'EUR', 'TWD', 'OTHER']"
      validation="required"
    />
    <FormKit
      type="number"
      help="Exchange rate on invoice date"
      label="exchange rate"
      name="invoice_exchange_rate"
      validation="required"
      step="0.01"
    />

    <FormKit
      type="number"
      label="Amount"
      help="Amount shown on invoice"
      name="invoice_amount"
      validation="required"
      step="0.01"
    />

    <FormKit
      type="number"
      name="invoice_twd_amount"
      label="Invoice amount (TWD)"
      validation="required"
      min="1"
      step="1"
      validation-visibility="live"
    />

    <FormKit
      v-if="actionOptions.length"
      type="select"
      label="Action"
      help="Save/ Delete"
      v-model="action"
      :options="actionOptions"
    />
  </FormKit>
</template>
